/**
 * Server-state hooks for webhook endpoints (C16 API) via TanStack Query.
 *
 * The signing secret is returned ONLY by the create mutation
 * ({@link CreateWebhookEndpointResponse}). It is never cached into the list
 * query and never re-fetched, so the UI can show it exactly once.
 */
import {
  type CreateWebhookEndpointRequest,
  type CreateWebhookEndpointResponse,
  type ListWebhookEndpointsResponse,
  type TestWebhookResponse,
} from "@records-monitor/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/api/useApiClient";

export const webhooksKey = ["notifications", "webhooks"] as const;

/** GET /notifications/webhooks — secrets are never included. */
export function useWebhooks() {
  const api = useApiClient();
  return useQuery({
    queryKey: webhooksKey,
    queryFn: () => api.get<ListWebhookEndpointsResponse>("/notifications/webhooks"),
  });
}

/** POST /notifications/webhooks — returns the secret exactly once. */
export function useCreateWebhook() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateWebhookEndpointRequest) =>
      api.post<CreateWebhookEndpointResponse>("/notifications/webhooks", body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: webhooksKey });
    },
  });
}

/** DELETE /notifications/webhooks/:id. */
export function useDeleteWebhook() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<undefined>(`/notifications/webhooks/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: webhooksKey });
    },
  });
}

/** POST /notifications/webhooks/:id/test — send a signed test delivery. */
export function useTestWebhook() {
  const api = useApiClient();
  return useMutation({
    mutationFn: (id: string) => api.post<TestWebhookResponse>(`/notifications/webhooks/${id}/test`),
  });
}
